import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, mergeMap, catchError, switchMap, withLatestFrom, filter } from 'rxjs/operators';
import { of, from } from 'rxjs';
import { Store, select } from '@ngrx/store';

import * as fromActions from '../actions/offline-statuses.actions';
import { OfflineStatusesService } from '../../services/offline-statuses.service';
import { selectOfflineState } from '../selectors/offline.selectors';
import { getSelectedAsset } from '../selectors/delivery-asset.selectors';
import { DeliveryAsset } from '../../models/delivery-asset.model';
import { removeOfflineDelivery } from '../actions/offline.actions';
import { TogglePlaying } from '../actions/delivery-assets.actions';

@Injectable()
export class OfflineStatusEffects {
   currentCampaignTrackId;

  actions$ = inject(Actions);
  offlineStatusesService = inject(OfflineStatusesService);
  store = inject(Store);

  sendOfflineDeleteStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeOfflineDelivery),
      withLatestFrom(this.store.pipe(select(selectOfflineState))),
      map(([data, isOffline]) => {
        const { deliveryKeyCode } = data;
        return { deliveryKeyCode, isOffline };
      }),
      mergeMap(({ deliveryKeyCode, isOffline }) =>
        this.offlineStatusesService.setDeleteEventStatus(deliveryKeyCode, isOffline).pipe(
          map(() => fromActions.sendOfflineDeleteStatusSuccess()),
          catchError(() => of(fromActions.sendOfflineDeleteStatusFail())),
        ),
      ),
    ),
  );

  sendOfflineDwnlStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.sendOfflineDwnlStatus),
      withLatestFrom(this.store.pipe(select(selectOfflineState))),
      map(([data, isOffline]) => {
        const { asset, eventStatus } = data;
        return { asset, eventStatus, isOffline };
      }),
      mergeMap(({ asset, eventStatus, isOffline }) =>
        this.offlineStatusesService.setAssetDownloadEventStatus(asset, isOffline, eventStatus).pipe(
          map(() =>
            fromActions.sendOfflineDwnlStatusSuccess({
              eventStatus,
              deliveryKeyCode: asset.deliveryKeyCode,
            }),
          ),
          catchError(() => of(fromActions.sendOfflineDwnlStatusFail())),
        ),
      ),
    ),
  );

  sendOfflinePlayStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TogglePlaying),
      switchMap(() => this.store.pipe(select(getSelectedAsset))),
      filter((asset: DeliveryAsset) => {
        const trackAssetType = this.getAudioTrackAssetType(asset);
        return this.isAudioAssetPlaying(trackAssetType, asset);
      }),
      withLatestFrom(this.store.pipe(select(selectOfflineState))),
      map(([asset, isOffline]) => {
        this.currentCampaignTrackId = asset.campaignTrackId;
        return { asset, isOffline };
      }),
      mergeMap(({ asset, isOffline }) =>
        this.offlineStatusesService.setAssetPlayEventStatus(asset, isOffline).pipe(
          map(() => fromActions.sendOfflinePlayStatusSuccess()),
          catchError(() => of(fromActions.sendOfflinePlayStatusFail())),
        ),
      ),
    ),
  );

  sendOfflineStoredEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.sendOfflineStoredEvents),
      mergeMap(() =>
        from(this.offlineStatusesService.sendOfflineStoredEvents()).pipe(
          map(() => fromActions.sendOfflineStoredEventsSuccess()),
          catchError(() => of(fromActions.sendOfflineStoredEventsFail())),
        ),
      ),
    ),
  );

  isAudioAssetPlaying(trackAssetType, asset): boolean {
    return (
      trackAssetType &&
      trackAssetType.url.includes('file') &&
      asset.mediaType === 'AUDIO' &&
      asset.isPlaying &&
      asset.campaignTrackId !== this.currentCampaignTrackId
    );
  }

  getAudioTrackAssetType(asset) {
    return (
      asset && asset.types && asset.types.find((trackType: any) => trackType.type === 'Streaming')
    );
  }
}
